<template>
    <a-spin tip="Carregando..." :spinning="spinning">
        <div class="container">
            <a-card>
                <template #title>
                    <a-breadcrumb separator=">">
                        <a-breadcrumb-item>
                            <FileOutlined />
                            <a><span>Camadas Temáticas</span></a>
                        </a-breadcrumb-item>
                        <a-breadcrumb-item>Listar Camadas Temáticas</a-breadcrumb-item>
                    </a-breadcrumb>
                </template>
                <template #extra>
                    <a-button type="primary" :icon="h(PlusOutlined)" @click="newRegister">
                        Nova Camada Temática
                    </a-button>
                </template>

                <div class="table-toolbar">
                    <h3>Camadas Temáticas</h3>
                    <a-input-search
                        v-model:value="search"
                        placeholder="Filtrar por nome"
                        allowClear
                        :enter-button="false"
                        style="max-width: 320px"
                    />
                </div>

                <a-table
                    :data-source="filteredData"
                    :columns="columns"
                    :loading="loading"
                    class="components-table-demo-nested"
                    row-key="id"
                    :pagination="{ pageSize: 10 }"
                >
                    <template #bodyCell="{ column, record: layer }">
                        <template v-if="column.key === 'name'">
                            <div class="layer-name">#{{ layer.payload_header.id }}</div>
                            <div class="layer-title">{{ layer.payload_header.name }}</div>
                        </template>

                        <template v-else-if="column.key === 'status'">
                            <a-tag :color="statusColor(layer.status)">
                                {{ statusText(layer.status) }}
                            </a-tag>
                        </template>

                        <template v-else-if="column.key === 'progress'">
                            <div class="progress-cell">
                                <a-tooltip v-if="canPause(layer)" title="Pausar processamento">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        size="small"
                                        class="center-flex pause"
                                        @click="onPause(layer)"
                                    >
                                        <template #icon><PauseCircleOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                                <div class="progress-content">
                                    <a-progress
                                        :percent="progressOf(layer)"
                                        :status="progressStatus(layer.status)"
                                        size="small"
                                    />
                                    <div class="schedule-text">{{ scheduleText(layer) }}</div>
                                </div>
                            </div>
                        </template>

                        <template v-else-if="column.key === 'actions'">
                            <a-space :size="4" class="actions-row">
                                <a-tooltip title="Ver">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex view"
                                        @click="onView(layer)"
                                    >
                                        <template #icon><EyeOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip title="Alterar">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex edit"
                                        @click="onEdit(layer)"
                                    >
                                        <template #icon><FormOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip title="Reprocessar / Executar">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex reprocess"
                                        @click="onExecute(layer)"
                                    >
                                        <template #icon><ReloadOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip title="Vincular Tutorial">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex faq"
                                        @click="onTutorial(layer)"
                                    >
                                        <template #icon><BookOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip title="Permissões">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex permission"
                                        @click="onPermissions(layer)"
                                    >
                                        <template #icon><SafetyOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip :title="isPublicMap(layer) ? 'Copiar link público' : 'Mapa não é público'">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex copy-link"
                                        :disabled="!isPublicMap(layer)"
                                        @click="copyPublicLink(layer)"
                                    >
                                        <template #icon><CopyOutlined /></template>
                                    </a-button>
                                </a-tooltip>

                                <a-tooltip title="Excluir">
                                    <a-button
                                        type="primary"
                                        danger
                                        shape="circle"
                                        class="center-flex delete"
                                        @click="confirmDelete(layer)"
                                    >
                                        <template #icon><DeleteOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                            </a-space>
                        </template>
                    </template>
                </a-table>

                <a-modal
                    v-model:open="tutorialModalOpen"
                    :title="tutorialModalTitle"
                    :footer="null"
                    @cancel="closeTutorial"
                    destroyOnClose
                >
                    <FaqPicker
                        :selected-id="modalRow?.payload_header.faq_id || null"
                        @save="handleFaqSave"
                        @cancel="closeTutorial"
                    />
                </a-modal>

                <a-modal
                    v-model:open="permissionsModalOpen"
                    :title="permissionsModalTitle"
                    :footer="null"
                    @cancel="closePermissions"
                    destroyOnClose
                >
                    <UserGroupPicker
                        :selected-ids="modalRow?.payload_header.users_groups_ids || []"
                        @save="handlePermissionsSave"
                        @cancel="closePermissions"
                    />
                </a-modal>
            </a-card>
        </div>
    </a-spin>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, h, computed } from 'vue'
import {
    EyeOutlined,
    FormOutlined,
    CopyOutlined,
    ReloadOutlined,
    BookOutlined,
    SafetyOutlined,
    PlusOutlined,
    FileOutlined,
    DeleteOutlined,
    PauseCircleOutlined,
} from '@ant-design/icons-vue'
import { Modal } from 'ant-design-vue'
import UserGroupPicker from '../../Components/UserGroupPicker.vue'
import FaqPicker from '../../Components/FaqPicker.vue'
import { openNotification } from '../../../../utils/utilities.js'

const spinning = ref(false)
const tutorialModalOpen = ref(false)
const permissionsModalOpen = ref(false)
const modalRow = ref(null)
const search = ref('')
const loading = ref(false)
const rows = ref([])

const filteredData = computed(() => {
    const term = (search.value || '').trim().toLowerCase()
    if (!term) return rows.value

    return rows.value.filter(r => r.payload_header.name.trim().toLowerCase().includes(term))
})

const newRegister = () => {
    window.location.href = '/maps/thematic-layers/create'
}

const columns = [
    { title: 'Nome', key: 'name', width: 200 },
    { title: 'Status', key: 'status', width: 150 },
    { title: 'Processamento', key: 'progress' },
    { title: 'Ações', key: 'actions', width: 300, align: 'right' },
]

let timer = null
const fetchRows = async () => {
    loading.value = true
    try {
        const { data } = await window.axios.get('/api/maps/thematic-layers/processing')
        rows.value = data?.data || []
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchRows()
    timer = setInterval(fetchRows, 5000)
})

onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
})

const progressOf = (rec) => rec?.batch?.progress ?? 0

const statusText = (st) => ({
    done: 'Finalizado com sucesso',
    'in progress': 'Executando',
    failed: 'Erro no Processamento',
    cancelled: 'Processamento pausado',
}[st] || 'Aguardando')

const statusColor = (st) => ({
    done: 'green',
    'in progress': 'blue',
    failed: 'red',
    cancelled: 'orange',
}[st] || 'default')

const progressStatus = (st) =>
    st === 'failed' || st === 'cancelled'
        ? 'exception'
        : st === 'in progress'
            ? 'active'
            : 'normal'

const dias = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sabado']

const scheduleText = (rec) => {
    const t = rec?.task
    const a = rec?.task_args || {}
    if (!t || t === '0') return 'Sem agendamento realizado'
    if (t === 'dailyAt') return `Agendado diariamente às ${a.hour}`
    if (t === 'weeklyOn') return `Agendado semanalmente ${dias[a.weekly]} às ${a.hour}`
    if (t === 'monthlyOn') return `Agendado mensalmente dia ${a.monthly} às ${a.hour}`
    return ''
}

function onView(row) {
    window.open(`/maps/thematic-layers/${row.id_processamento}/view`, '_blank')
}

const onEdit = (layer) => {
    window.location.href = `/maps/thematic-layers/${layer.id_processamento}/edit`
}

const onExecute = async (layer) => {
    try {
        spinning.value = true
        await window.axios.get(`/api/maps/thematic-layers/${layer.id_processamento}/dispatch`)
        openNotification('success', 'Enviado para a fila!')
        await fetchRows()
    } catch (e) {
        openNotification('error', e?.response?.data?.message || e.message || 'Falha ao reprocessar!')
    } finally {
        spinning.value = false
    }
}

const canPause = (layer) => layer?.status === 'in progress'

const onPause = async (layer) => {
    if (!canPause(layer)) return

    try {
        spinning.value = true
        const { data } = await window.axios.post(`/api/maps/thematic-layers/${layer.id_processamento}/cancel`)
        openNotification('success', data?.message || 'Processamento pausado!')
        await fetchRows()
    } catch (e) {
        openNotification('error', e?.response?.data?.message || e.message || 'Falha ao pausar processamento!')
    } finally {
        spinning.value = false
    }
}

const confirmDelete = (layer) => {
    const name = layer?.payload_header?.name || `#${layer?.id_processamento}`
    Modal.confirm({
        title: 'Excluir camada temática?',
        content: `Tem certeza que deseja excluir "${name}"? Serão removidos o registro, a fila de processamento e a tabela gerada. Esta ação não pode ser desfeita.`,
        okText: 'Excluir',
        okType: 'danger',
        cancelText: 'Cancelar',
        centered: true,
        async onOk() {
            await onDelete(layer)
        },
    })
}

const onDelete = async (layer) => {
    try {
        spinning.value = true
        const { data } = await window.axios.delete(`/api/maps/thematic-layers/${layer.id_processamento}`)
        openNotification('success', data?.message || 'Excluído com sucesso!')
        await fetchRows()
    } catch (e) {
        openNotification('error', e?.response?.data?.message || e.message || 'Falha ao excluir!')
        await fetchRows()
    } finally {
        spinning.value = false
    }
}

const tutorialModalTitle = computed(() =>
    modalRow.value
        ? `Vincular Tutorial — ${modalRow.value.payload_header.descricao}`
        : 'Vincular Tutorial'
)

const permissionsModalTitle = computed(() =>
    modalRow.value
        ? `Permissões — ${modalRow.value.payload_header.descricao}`
        : 'Permissões'
)

function onTutorial(row) {
    modalRow.value = row
    tutorialModalOpen.value = true
}

function onPermissions(row) {
    modalRow.value = row
    permissionsModalOpen.value = true
}

function closeTutorial() {
    tutorialModalOpen.value = false
    modalRow.value = null
}

function closePermissions() {
    permissionsModalOpen.value = false
    modalRow.value = null
}

async function handleFaqSave(faqId) {
    const id = modalRow.value?.id_processamento
    try {
        const response = await window.axios.put(`/api/maps/thematic-layers/${id}`, { faq_id: faqId })
        openNotification('success', response.data.message || 'Camada atualizada!')
        await fetchRows()
    } catch (e) {
        openNotification('error', e.message || 'Erro ao atualizar!')
        await fetchRows()
    }
    closeTutorial()
}

async function handlePermissionsSave(groupIds) {
    const id = modalRow.value?.id_processamento
    try {
        const response = await window.axios.put(`/api/maps/thematic-layers/${id}`, { users_groups: groupIds })
        openNotification('success', response.data.message || 'Camada atualizada!')
        await fetchRows()
    } catch (e) {
        openNotification('error', e.message || 'Erro ao atualizar!')
        await fetchRows()
    }
    closePermissions()
}

function getPublicLink(layer) {
    const id = layer?.id_processamento ?? layer?.id
    return `${window.location.origin}/maps/public/thematic-layers/${id}/view`
}

function isPublicMap(layer) {
    return Boolean(layer?.payload_header?.is_public ?? layer?.is_public)
}

async function copyPublicLink(layer) {
    const link = getPublicLink(layer)
    try {
        if (!navigator?.clipboard?.writeText) throw new Error('Clipboard API indisponível')
        await navigator.clipboard.writeText(link)
        openNotification('success', 'Link público copiado para a área de transferência.')
    } catch (_) {
        openNotification('error', 'Não foi possível copiar o link público.')
    }
}
</script>

<style scoped>
.table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 30px 0 12px 0;
}

.layer-name {
    color: #8c8c8c;
    font-size: 12px;
}

.layer-title {
    font-weight: 500;
}

.schedule-text {
    margin-top: 4px;
    color: #8c8c8c;
    font-size: 12px;
}

.progress-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.progress-content {
    flex: 1;
    min-width: 0;
}

.actions-row {
    flex-wrap: nowrap !important;
    white-space: nowrap;
}

.center-flex {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.view { background-color: green; }
.edit { background-color: blue; }
.reprocess { background-color: orange; }
.pause { background-color: #fa8c16; flex-shrink: 0; }
.delete { background-color: #ff4d4f; }
.faq { background-color: black; }
.permission { color: black; background-color: yellow; }
.copy-link { background-color: #6f42c1; }

.copy-link:disabled,
.copy-link[disabled] {
    opacity: 0.45 !important;
    cursor: not-allowed !important;
}
</style>
